import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, LogOut, History, Check, X, Loader2, MessageSquare, 
  Sparkles, RotateCcw, FileText, Users, Settings2, ChevronRight,
  Plus, Minus, ChevronDown, Mic, MicOff, Square
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AI_CONFIG = {
  gpt: { name: 'GPT-4o', color: '#10B981', provider: 'OpenAI' },
  claude: { name: 'Claude', color: '#F97316', provider: 'Anthropic' },
  gemini: { name: 'Gemini', color: '#3B82F6', provider: 'Google' }
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading, isAuthenticated } = useAuth();
  
  // Thread state
  const [conversationId, setConversationId] = useState(null);
  const [thread, setThread] = useState([]); // Array of {round, question, responses, timestamp}
  const [selectedAIs, setSelectedAIs] = useState(['gpt', 'claude', 'gemini']);
  const [visibleResponseAIs, setVisibleResponseAIs] = useState([]);
  const [assistantMessages, setAssistantMessages] = useState([]);
  const [activeMode, setActiveMode] = useState('compare');
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [assistantLoading, setAssistantLoading] = useState(false);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // UI state
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [showAISelector, setShowAISelector] = useState(false);
  
  // Synthesis
  const [synthesis, setSynthesis] = useState(null);
  const [synthesisLoading, setSynthesisLoading] = useState(false);
  const [showSynthesis, setShowSynthesis] = useState(false);
  
  // Discussion with streaming
  const [discussionResult, setDiscussionResult] = useState(null);
  const [discussionLoading, setDiscussionLoading] = useState(false);
  const [showDiscussionSettings, setShowDiscussionSettings] = useState(false);
  const [discussionMode, setDiscussionMode] = useState('consensus');
  const [discussionRounds, setDiscussionRounds] = useState(3);
  const [discussionAIs, setDiscussionAIs] = useState([]);
  const [liveExchanges, setLiveExchanges] = useState([]);
  const [discussionStatus, setDiscussionStatus] = useState('');
  const [showDiscussion, setShowDiscussion] = useState(false);
  
  const threadEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom of thread
  useEffect(() => {
    if (threadEndRef.current) {
      threadEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [thread, liveExchanges, assistantMessages, assistantLoading]);

  // Update discussion AIs when thread changes
  useEffect(() => {
    if (thread.length > 0) {
      const lastRound = thread[thread.length - 1];
      setDiscussionAIs(lastRound.responses.map(r => r.ai_key));
    }
  }, [thread]);

  useEffect(() => {
    const available = Array.from(new Set(thread.flatMap(item => item.responses.map(response => response.ai_key))));

    if (available.length === 0) {
      setVisibleResponseAIs([]);
      return;
    }

    setVisibleResponseAIs(prev => {
      if (prev.length === 0) {
        return available;
      }

      const next = prev.filter(ai => available.includes(ai));
      return next.length > 0 ? next : available;
    });
  }, [thread]);

  const toggleAI = (ai) => {
    setSelectedAIs(prev => {
      if (prev.includes(ai)) {
        if (prev.length === 1) {
          toast.error('Gardez au moins une IA sélectionnée');
          return prev;
        }
        return prev.filter(a => a !== ai);
      }
      return [...prev, ai];
    });
  };

  const toggleVisibleAI = (ai) => {
    setVisibleResponseAIs(prev => {
      if (prev.includes(ai)) {
        if (prev.length === 1) {
          toast.error('Gardez au moins une IA visible');
          return prev;
        }
        return prev.filter(item => item !== ai);
      }

      return [...prev, ai];
    });
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.info('🎤 Enregistrement en cours...');
    } catch (error) {
      console.error('Microphone error:', error);
      toast.error('Impossible d\'accéder au microphone. Vérifiez les permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    const transcribeToast = toast.loading('Transcription en cours...');

    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');

      const response = await axios.post(`${API}/api/voice/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success && response.data.text) {
        setQuestion(prev => prev ? `${prev} ${response.data.text}` : response.data.text);
        toast.dismiss(transcribeToast);
        toast.success('Transcription réussie !');
        inputRef.current?.focus();
      } else {
        throw new Error('Transcription vide');
      }
    } catch (error) {
      console.error('Transcription error:', error);
      toast.dismiss(transcribeToast);
      toast.error(error.response?.data?.detail || 'Erreur de transcription');
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleQuery = async () => {
    if (!question.trim()) {
      toast.error('Veuillez entrer une question');
      return;
    }
    if (selectedAIs.length === 0) {
      toast.error('Sélectionnez au moins une IA');
      return;
    }

    setLoading(true);
    setSynthesis(null);
    setShowSynthesis(false);
    setDiscussionResult(null);
    setShowDiscussion(false);
    
    // Show loading toast with AI names
    const aiNames = selectedAIs.map(ai => AI_CONFIG[ai]?.name || ai).join(', ');
    const loadingToast = toast.loading(`Interrogation de ${aiNames}...`);

    try {
      // Build history from thread for context (only last 2 for speed)
      const history = thread.slice(-2).map(t => ({
        question: t.question,
        responses: t.responses.map(r => ({ ai_key: r.ai_key, content: r.content }))
      }));

      let response;
      if (isAuthenticated) {
        response = await api.post('/ai/thread', {
          conversation_id: conversationId,
          question: question,
          selected_ais: selectedAIs,
          history: history
        });
      } else {
        response = await axios.post(`${API}/api/ai/guest-thread`, {
          conversation_id: conversationId,
          question: question,
          selected_ais: selectedAIs,
          history: history
        });
      }
      
      toast.dismiss(loadingToast);

      // Update state
      setConversationId(response.data.conversation_id);
      
      const newRound = {
        round: response.data.round,
        question: question,
        responses: response.data.responses,
        selectedAIs: response.data.selected_ais,
        timestamp: new Date().toISOString()
      };
      
      setThread(prev => [...prev, newRound]);
      setQuestion('');
      toast.success('Réponses reçues !');
      
      // Focus back on input for next question
      setTimeout(() => inputRef.current?.focus(), 100);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la requête');
    } finally {
      setLoading(false);
    }
  };

  const handleAssistantQuery = async () => {
    if (!question.trim()) {
      toast.error('Veuillez entrer une question');
      return;
    }

    setAssistantLoading(true);
    const assistantToast = toast.loading('Synapsea prépare sa réponse...');

    try {
      const payload = {
        conversation_id: conversationId,
        question,
        comparator_thread: thread,
        assistant_history: assistantMessages.map(message => ({
          role: message.role,
          content: message.content,
          timestamp: message.timestamp
        })),
        visible_ais: visibleResponseAIs
      };

      const response = isAuthenticated
        ? await api.post('/assistant/thread', payload)
        : await axios.post(`${API}/api/assistant/guest-thread`, payload);

      toast.dismiss(assistantToast);
      setConversationId(response.data.conversation_id);
      setAssistantMessages(prev => [...prev, response.data.user_message, response.data.assistant_message]);
      setQuestion('');
      toast.success('Synapsea vous répond');
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (error) {
      toast.dismiss(assistantToast);
      toast.error(error.response?.data?.detail || 'Erreur avec Synapsea');
    } finally {
      setAssistantLoading(false);
    }
  };

  const handleSynthesis = async () => {
    if (thread.length === 0) {
      toast.error('Aucune conversation à synthétiser');
      return;
    }

    setSynthesisLoading(true);
    try {
      // Gather all responses from all rounds
      const allResponses = [];
      thread.forEach(t => {
        t.responses.forEach(r => {
          allResponses.push({
            ai_key: r.ai_key,
            content: `[Round ${t.round}] ${r.content}`
          });
        });
      });

      // Use the last question as the topic
      const topic = thread.map(t => t.question).join(' → ');
      
      const response = await axios.post(`${API}/api/ai/thread-synthesis`, {
        question: topic,
        responses: allResponses
      });
      
      setSynthesis(response.data.synthesis);
      setShowSynthesis(true);
      toast.success('Synthèse générée !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la synthèse');
    } finally {
      setSynthesisLoading(false);
    }
  };

  const handleDiscussion = async () => {
    if (discussionAIs.length < 2) {
      toast.error('Sélectionnez au moins 2 IA');
      return;
    }

    setShowDiscussionSettings(false);
    setDiscussionLoading(true);
    setShowDiscussion(true);
    setLiveExchanges([]);
    setDiscussionResult(null);
    setDiscussionStatus('Démarrage de la discussion...');

    try {
      // Get latest responses for discussion
      const lastRound = thread[thread.length - 1];
      const responsesData = lastRound.responses
        .filter(r => discussionAIs.includes(r.ai_key))
        .map(r => ({ ai_key: r.ai_key, content: r.content }));

      const response = await fetch(`${API}/api/ai/discussion-stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: lastRound.question,
          responses: responsesData,
          mode: discussionMode,
          max_rounds: discussionRounds,
          participating_ais: discussionAIs
        })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              switch (data.type) {
                case 'init':
                  setDiscussionStatus('Discussion initialisée');
                  break;
                case 'round_start':
                  setDiscussionStatus(`Round ${data.round}/${data.total_rounds}`);
                  break;
                case 'ai_thinking':
                  setDiscussionStatus(`${data.ai_name} réfléchit...`);
                  break;
                case 'exchange':
                  setLiveExchanges(prev => [...prev, data.exchange]);
                  break;
                case 'round_end':
                  setDiscussionStatus(`Round ${data.round} terminé`);
                  break;
                case 'generating_analysis':
                  setDiscussionStatus('Génération de l\'analyse finale...');
                  break;
                case 'complete':
                  setDiscussionResult({
                    final_analysis: data.final_analysis,
                    discussion_rounds: data.discussion_rounds
                  });
                  setDiscussionStatus('Discussion terminée !');
                  setDiscussionLoading(false);
                  toast.success('Discussion terminée !');
                  break;
                default:
                  break;
              }
            } catch (e) {
              console.error('Parse error:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Discussion error:', error);
      toast.error('Erreur lors de la discussion');
      setDiscussionLoading(false);
    }
  };

  const resetConversation = () => {
    setThread([]);
    setConversationId(null);
    setAssistantMessages([]);
    setQuestion('');
    setActiveMode('compare');
    setVisibleResponseAIs([]);
    setSynthesis(null);
    setShowSynthesis(false);
    setDiscussionResult(null);
    setShowDiscussion(false);
    setLiveExchanges([]);
    toast.info('Nouvelle conversation');
  };

  const loadHistory = async () => {
    if (!isAuthenticated) {
      toast.info('Connectez-vous pour voir l\'historique');
      navigate('/login');
      return;
    }
    try {
      const response = await api.get('/conversations');
      setConversations(response.data.conversations);
      setShowHistory(true);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const loadConversation = async (convId) => {
    try {
      const response = await api.get(`/ai/thread/${convId}`);
      setConversationId(convId);
      setThread(response.data.thread || []);
      setAssistantMessages(response.data.assistant_messages || []);
      if (response.data.conversation?.selected_ais) {
        setSelectedAIs(response.data.conversation.selected_ais);
      }
      setShowHistory(false);
      toast.success('Conversation chargée');
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-emerald-400" />
      </div>
    );
  }

  const renderContent = (content) => {
    return content.split('\n').map((line, i) => {
      if (line.startsWith('## ')) return <h2 key={i} className="text-lg font-bold text-white mt-4 mb-2">{line.replace('## ', '')}</h2>;
      if (line.startsWith('### ')) return <h3 key={i} className="text-md font-semibold text-white/90 mt-3 mb-1">{line.replace('### ', '')}</h3>;
      if (line.startsWith('- **') || line.startsWith('**')) {
        const parts = line.split('**');
        return <p key={i} className="text-white/70 mb-1">{parts.map((part, j) => j % 2 === 1 ? <strong key={j} className="text-white">{part}</strong> : part)}</p>;
      }
      if (line.startsWith('- ')) return <li key={i} className="text-white/70 ml-4 mb-1">{line.replace('- ', '')}</li>;
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} className="text-white/70 mb-1">{line}</p>;
    });
  };

  const hasThread = thread.length > 0;
  const hasAssistantMessages = assistantMessages.length > 0;
  const hasAnyConversation = hasThread || hasAssistantMessages;
  const isSubmitting = loading || assistantLoading;
  const availableResponseAIs = Array.from(new Set(thread.flatMap(item => item.responses.map(response => response.ai_key))));
  const timelineItems = [
    ...thread.map(round => ({
      type: 'compare-round',
      key: `compare-${round.round}`,
      timestamp: round.timestamp,
      round
    })),
    ...assistantMessages.map(message => ({
      type: 'assistant-message',
      key: message.message_id,
      timestamp: message.timestamp,
      message
    }))
  ].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  return (
    <div className="min-h-screen bg-[#05050A]" data-testid="dashboard-page">
      {/* Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[150px]" />
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-3 sm:px-6 py-3 sm:py-4 glass">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-blue-500 flex items-center justify-center">
              <Sparkles className="w-4 sm:w-5 h-4 sm:h-5 text-white" />
            </div>
            <span className="font-outfit font-bold text-lg sm:text-xl text-white">Synapsea</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {isAuthenticated && (
              <button onClick={loadHistory} className="btn-secondary text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" data-testid="history-btn">
                <History className="w-4 h-4" /> <span className="hidden sm:inline">Historique</span>
              </button>
            )}
            {isAuthenticated ? (
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="hidden md:block text-sm text-white/70">{user?.name}</span>
                <button onClick={handleLogout} className="p-2 rounded-full hover:bg-white/5" data-testid="logout-btn">
                  <LogOut className="w-5 h-5 text-white/50" />
                </button>
              </div>
            ) : (
              <button onClick={() => navigate('/login')} className="btn-primary text-xs sm:text-sm px-3 py-1.5" data-testid="login-btn">Connexion</button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 pt-20 sm:pt-24 pb-72 sm:pb-64 px-3 sm:px-4 max-w-5xl mx-auto">
        
        {/* AI Selector Bar */}
        <div className="glass-card rounded-xl sm:rounded-2xl p-4 sm:p-5 mb-4 sm:mb-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <span className="label text-emerald-400 text-[10px] sm:text-xs">Mode de conversation</span>
              <h2 className="font-outfit font-bold text-xl sm:text-2xl text-white mt-2">Passez du comparateur à Synapsea sans friction</h2>
              <p className="text-sm text-white/50 mt-2 max-w-2xl">
                {activeMode === 'compare'
                  ? 'Le comparateur interroge vos IA sélectionnées en parallèle.'
                  : 'Synapsea reprend vos réponses visibles et vous les explique simplement.'}
              </p>
            </div>
            <div className="inline-flex rounded-full bg-white/5 p-1 border border-white/10 self-start" data-testid="conversation-mode-switcher">
              <button
                onClick={() => setActiveMode('compare')}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${activeMode === 'compare' ? 'bg-emerald-500 text-white' : 'text-white/60 hover:text-white'}`}
                data-testid="mode-compare-button"
              >
                Comparateur
              </button>
              <button
                onClick={() => setActiveMode('assistant')}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${activeMode === 'assistant' ? 'bg-sky-500 text-white' : 'text-white/60 hover:text-white'}`}
                data-testid="mode-assistant-button"
              >
                Assistant Synapsea
              </button>
            </div>
          </div>

          {hasThread && availableResponseAIs.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="label text-[10px] sm:text-xs text-white/40 mr-2">Filtrer les réponses visibles</span>
                <button
                  onClick={() => setVisibleResponseAIs(availableResponseAIs)}
                  className={`px-3 py-1.5 rounded-full text-xs sm:text-sm border transition-colors ${visibleResponseAIs.length === availableResponseAIs.length ? 'border-white/30 bg-white/10 text-white' : 'border-white/10 text-white/60 hover:text-white hover:border-white/20'}`}
                  data-testid="response-filter-all-button"
                >
                  Toutes
                </button>
                {availableResponseAIs.map(ai => {
                  const config = AI_CONFIG[ai];
                  const isActive = visibleResponseAIs.includes(ai);
                  return (
                    <button
                      key={`filter-${ai}`}
                      onClick={() => toggleVisibleAI(ai)}
                      className={`px-3 py-1.5 rounded-full text-xs sm:text-sm border transition-all ${isActive ? 'text-white' : 'text-white/45 border-white/10 hover:border-white/20 hover:text-white/70'}`}
                      style={{
                        borderColor: isActive ? `${config.color}80` : undefined,
                        backgroundColor: isActive ? `${config.color}22` : 'transparent'
                      }}
                      data-testid={`response-filter-${ai}-button`}
                    >
                      {config.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="glass-card rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <span className="label text-emerald-400 text-[10px] sm:text-xs">IA actives du comparateur</span>
            <button 
              onClick={() => setShowAISelector(!showAISelector)}
              className="text-[10px] sm:text-xs text-white/50 hover:text-white/80 flex items-center gap-1"
              data-testid="toggle-ai-selector-button"
            >
              Modifier <ChevronDown className={`w-3 sm:w-4 h-3 sm:h-4 transition-transform ${showAISelector ? 'rotate-180' : ''}`} />
            </button>
          </div>
          
          {/* Active AIs */}
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {selectedAIs.map(ai => {
              const config = AI_CONFIG[ai];
              return (
                <div key={ai} className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm" style={{ backgroundColor: `${config.color}20`, border: `1px solid ${config.color}40` }}>
                  <div className="w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full" style={{ backgroundColor: config.color }} />
                  <span className="text-white">{config.name}</span>
                  {showAISelector && (
                    <button onClick={() => toggleAI(ai)} className="ml-0.5 sm:ml-1 text-white/50 hover:text-white">
                      <Minus className="w-3 h-3" />
                    </button>
                  )}
                </div>
              );
            })}
            
            {/* Add AI button when selector is open */}
            {showAISelector && Object.entries(AI_CONFIG).filter(([key]) => !selectedAIs.includes(key)).map(([key, config]) => (
              <button
                key={key}
                onClick={() => toggleAI(key)}
                className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full border border-dashed border-white/20 hover:border-white/40 text-white/50 hover:text-white/80 text-xs sm:text-sm"
              >
                <Plus className="w-3 h-3" />
                <span className="text-sm">{config.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Chat Thread View */}
        <div className="space-y-4 sm:space-y-6 mb-6">
          {!hasAnyConversation ? (
            /* Empty state */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center py-12 sm:py-20"
            >
              <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-blue-500/20 flex items-center justify-center mb-4 sm:mb-6">
                <MessageSquare className="w-8 sm:w-10 h-8 sm:h-10 text-emerald-400" />
              </div>
              <h3 className="font-outfit font-bold text-xl sm:text-2xl text-white mb-2">Commencez une conversation</h3>
              <p className="text-sm sm:text-base text-white/50 text-center max-w-md">
                Posez une question aux IA sélectionnées et continuez la discussion
              </p>
            </motion.div>
          ) : (
            /* Chat messages */
            timelineItems.map((item, index) => (
              <motion.div
                key={item.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3 sm:space-y-4"
              >
                {item.type === 'compare-round' ? (
                  <>
                    <div className="flex justify-end">
                      <div className="max-w-[85%] sm:max-w-[75%]">
                        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl rounded-tr-md px-4 sm:px-5 py-3 sm:py-4 shadow-lg shadow-emerald-500/20">
                          <p className="text-white text-sm sm:text-base">{item.round.question}</p>
                        </div>
                        <div className="flex justify-end mt-1">
                          <span className="text-[10px] sm:text-xs text-white/30">Vous • Comparateur • Q{item.round.round}</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 sm:space-y-3">
                      {item.round.responses
                        .filter(response => visibleResponseAIs.includes(response.ai_key))
                        .map((response, rIndex) => {
                          const config = AI_CONFIG[response.ai_key] || { color: '#8B909A', name: response.ai_name };
                          return (
                            <motion.div
                              key={response.message_id || rIndex}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: rIndex * 0.1 }}
                              className="flex justify-start"
                            >
                              <div className="max-w-[85%] sm:max-w-[75%]">
                                <button
                                  onClick={() => toggleVisibleAI(response.ai_key)}
                                  className="flex items-center gap-2 mb-1.5 group"
                                  data-testid={`response-bubble-toggle-${response.ai_key}`}
                                >
                                  <div className="w-6 sm:w-7 h-6 sm:h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${config.color}20` }}>
                                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: config.color }} />
                                  </div>
                                  <span className="text-xs sm:text-sm font-semibold text-white group-hover:text-white/80">{config.name}</span>
                                </button>
                                <div className="glass-card rounded-2xl rounded-tl-md px-4 sm:px-5 py-3 sm:py-4 border-l-2" style={{ borderColor: config.color }}>
                                  <p className="text-sm sm:text-base text-white/80 whitespace-pre-wrap leading-relaxed">{response.content}</p>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}

                      {item.round.responses.filter(response => visibleResponseAIs.includes(response.ai_key)).length === 0 && (
                        <div className="pl-2 text-xs sm:text-sm text-white/35" data-testid="filtered-round-empty-state">
                          Ce tour est masqué par votre filtre actuel.
                        </div>
                      )}
                    </div>
                  </>
                ) : item.message.role === 'user' ? (
                  <div className="flex justify-end">
                    <div className="max-w-[85%] sm:max-w-[75%]">
                      <div className="bg-gradient-to-r from-sky-500 to-cyan-500 rounded-2xl rounded-tr-md px-4 sm:px-5 py-3 sm:py-4 shadow-lg shadow-sky-500/20">
                        <p className="text-white text-sm sm:text-base">{item.message.content}</p>
                      </div>
                      <div className="flex justify-end mt-1">
                        <span className="text-[10px] sm:text-xs text-white/30">Vous • Synapsea</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-start">
                    <div className="max-w-[85%] sm:max-w-[75%]">
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-6 sm:w-7 h-6 sm:h-7 rounded-lg flex items-center justify-center bg-sky-500/20">
                          <Sparkles className="w-3.5 h-3.5 text-sky-300" />
                        </div>
                        <span className="text-xs sm:text-sm font-semibold text-white" data-testid="synapsea-assistant-label">Synapsea</span>
                      </div>
                      <div className="glass-card rounded-2xl rounded-tl-md px-4 sm:px-5 py-3 sm:py-4 border-l-2 border-sky-400/80 bg-sky-500/5">
                        <p className="text-sm sm:text-base text-white/85 whitespace-pre-wrap leading-relaxed">{item.message.content}</p>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            ))
          )}
          
          {/* Loading state while querying */}
          {loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-3"
            >
              {/* Typing indicators for each AI */}
              <div className="flex justify-start">
                <div className="glass-card rounded-2xl rounded-tl-md px-4 py-3 flex items-center gap-3">
                  <div className="flex gap-1">
                    {selectedAIs.map(ai => (
                      <div key={ai} className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: AI_CONFIG[ai]?.color }} />
                    ))}
                  </div>
                  <span className="text-sm text-white/50">Les IA réfléchissent...</span>
                  <Loader2 className="w-4 h-4 text-white/50 animate-spin" />
                </div>
              </div>
            </motion.div>
          )}

          {assistantLoading && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
              <div className="max-w-[85%] sm:max-w-[75%]">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-6 sm:w-7 h-6 sm:h-7 rounded-lg flex items-center justify-center bg-sky-500/20">
                    <Sparkles className="w-3.5 h-3.5 text-sky-300" />
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-white">Synapsea</span>
                </div>
                <div className="glass-card rounded-2xl rounded-tl-md px-4 py-3 flex items-center gap-3 border-l-2 border-sky-400/80 bg-sky-500/5">
                  <Loader2 className="w-4 h-4 text-sky-300 animate-spin" />
                  <span className="text-sm text-white/60">Synapsea prépare une explication…</span>
                </div>
              </div>
            </motion.div>
          )}
          
          <div ref={threadEndRef} />
          
          {/* Synthesis Result */}
          <AnimatePresence>
            {showSynthesis && synthesis && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="synthesis-card"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-blue-500 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-outfit font-bold text-lg text-white">Synthèse de la conversation</h3>
                  </div>
                  <button onClick={() => setShowSynthesis(false)} className="p-2 rounded-full hover:bg-white/10">
                    <X className="w-5 h-5 text-white/50" />
                  </button>
                </div>
                <div className="prose prose-invert max-w-none text-sm">{renderContent(synthesis.content)}</div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Discussion Result */}
          <AnimatePresence>
            {showDiscussion && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="glass-card rounded-2xl p-5"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-outfit font-bold text-lg text-white">Discussion IA</h3>
                      <span className="text-xs text-white/50">{discussionStatus}</span>
                    </div>
                  </div>
                  <button onClick={() => setShowDiscussion(false)} className="p-2 rounded-full hover:bg-white/10" disabled={discussionLoading}>
                    <X className="w-5 h-5 text-white/50" />
                  </button>
                </div>
                
                {/* Live exchanges */}
                <div className="space-y-3 mb-4 max-h-[400px] overflow-y-auto">
                  {liveExchanges.map((exchange, index) => {
                    const config = AI_CONFIG[exchange.ai_key] || { color: '#8B909A' };
                    return (
                      <motion.div 
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="pl-4 border-l-2 py-2"
                        style={{ borderColor: config.color }}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-white">{exchange.ai_name}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-white/40">R{exchange.round}</span>
                        </div>
                        <p className="text-sm text-white/70">{exchange.content}</p>
                      </motion.div>
                    );
                  })}
                  
                  {discussionLoading && (
                    <div className="flex items-center gap-2 text-white/50">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">{discussionStatus}</span>
                    </div>
                  )}
                </div>
                
                {/* Final analysis */}
                {discussionResult?.final_analysis && (
                  <div className="pt-4 border-t border-white/10">
                    <h4 className="font-semibold text-white mb-2">Analyse finale</h4>
                    <div className="text-sm text-white/70">{renderContent(discussionResult.final_analysis.content)}</div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
          
          <div ref={threadEndRef} />
        </div>

        {/* Action Buttons (when thread exists) */}
        {hasAnyConversation && (
          <div className="flex flex-wrap gap-3 mb-6 justify-center">
            {hasThread && (
              <>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSynthesis}
                  disabled={synthesisLoading}
                  className="btn-secondary text-sm"
                >
                  {synthesisLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  Synthèse globale
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    const lastRound = thread[thread.length - 1];
                    setDiscussionAIs(lastRound.responses.map(r => r.ai_key));
                    setShowDiscussionSettings(true);
                  }}
                  className="btn-secondary text-sm"
                >
                  <Users className="w-4 h-4" /> Faire discuter les IA
                </motion.button>
              </>
            )}
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={resetConversation}
              className="btn-secondary text-sm text-orange-400 border-orange-400/30 hover:border-orange-400/50"
            >
              <RotateCcw className="w-4 h-4" /> Nouvelle conversation
            </motion.button>
          </div>
        )}
      </main>

      {/* Fixed Input Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-t from-[#05050A] via-[#05050A] to-transparent">
        <div className="p-3 sm:p-4 pt-6 sm:pt-8">
          <div className="max-w-3xl mx-auto">
            <div className="glass-card rounded-xl sm:rounded-2xl p-3 sm:p-4">
              <div className="flex gap-2 sm:gap-3 items-end">
                <div className="flex-1">
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <span className={`text-[11px] sm:text-xs uppercase tracking-[0.2em] ${activeMode === 'compare' ? 'text-emerald-400' : 'text-sky-300'}`} data-testid="active-mode-label">
                      {activeMode === 'compare' ? 'Mode comparateur' : 'Mode assistant Synapsea'}
                    </span>
                    {hasThread && availableResponseAIs.length > 0 && (
                      <span className="text-[11px] sm:text-xs text-white/35" data-testid="visible-ai-count-label">
                        {visibleResponseAIs.length}/{availableResponseAIs.length} IA visibles
                      </span>
                    )}
                  </div>
                  <textarea
                  ref={inputRef}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (!isSubmitting) {
                        activeMode === 'assistant' ? handleAssistantQuery() : handleQuery();
                      }
                    }
                  }}
                  placeholder={activeMode === 'assistant' ? "Demandez une explication à Synapsea..." : hasThread ? "Posez une question de suivi aux IA..." : "Posez votre question aux IA..."}
                  className="flex-1 bg-transparent text-white text-sm sm:text-base placeholder:text-white/30 resize-none outline-none min-h-[44px] sm:min-h-[48px] max-h-[100px] sm:max-h-[120px]"
                  rows={1}
                  data-testid="question-input"
                  disabled={isRecording || isTranscribing || isSubmitting}
                  />
                </div>
                
                {/* Voice Recording Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isTranscribing || isSubmitting}
                  className={`p-2.5 sm:p-3 rounded-lg sm:rounded-xl transition-all duration-200 ${
                    isRecording 
                      ? 'bg-red-500 text-white animate-pulse' 
                      : isTranscribing
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid="voice-input-btn"
                  title={isRecording ? "Arrêter l'enregistrement" : "Parler pour écrire"}
                >
                  {isTranscribing ? (
                    <Loader2 className="w-4 sm:w-5 h-4 sm:h-5 animate-spin" />
                  ) : isRecording ? (
                    <Square className="w-4 sm:w-5 h-4 sm:h-5" />
                  ) : (
                    <Mic className="w-4 sm:w-5 h-4 sm:h-5" />
                  )}
                </motion.button>
                
                {/* Send Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={activeMode === 'assistant' ? handleAssistantQuery : handleQuery}
                  disabled={isSubmitting || !question.trim() || isRecording || (activeMode === 'compare' && selectedAIs.length === 0)}
                  className="btn-glow text-white p-2.5 sm:p-3 rounded-lg sm:rounded-xl disabled:opacity-50"
                  data-testid="submit-query-btn"
                  title={activeMode === 'assistant' ? 'Envoyer à Synapsea' : 'Envoyer au comparateur'}
                >
                  {isSubmitting ? <Loader2 className="w-4 sm:w-5 h-4 sm:h-5 animate-spin" /> : <Send className="w-4 sm:w-5 h-4 sm:h-5" />}
                </motion.button>
              </div>
              
              {/* Recording indicator */}
              {isRecording && (
                <div className="mt-2 flex items-center gap-2 text-red-400 text-xs sm:text-sm">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                  Enregistrement... Cliquez sur le carré pour arrêter
                </div>
              )}
              
              {/* Inscription suggestion for guests */}
              {!isAuthenticated && (
                <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-white/5 text-center">
                  <span className="text-white/40 text-xs sm:text-sm">
                    <button onClick={() => navigate('/register')} className="text-emerald-400 hover:text-emerald-300 underline">
                      Créez un compte gratuit
                    </button>
                    {' '}pour sauvegarder vos conversations
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Powered By MarketPulse */}
        <div className="powered-by pb-safe">
          <span className="powered-by-text">Powered By</span>
          <span className="powered-by-brand">MarketPulse</span>
        </div>
      </div>

      {/* Discussion Settings Modal */}
      <AnimatePresence>
        {showDiscussionSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDiscussionSettings(false)}
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="relative w-full max-w-lg glass rounded-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-white/5">
                <div className="flex items-center gap-3">
                  <Settings2 className="w-5 h-5 text-emerald-400" />
                  <h2 className="font-outfit font-bold text-xl text-white">Paramètres de discussion</h2>
                </div>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <label className="label text-white/50 mb-3 block">Mode de discussion</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setDiscussionMode('consensus')}
                      className={`p-4 rounded-xl text-left ${discussionMode === 'consensus' ? 'bg-emerald-500/20 border border-emerald-500' : 'glass-card'}`}
                    >
                      <div className="font-semibold text-white mb-1">Consensus</div>
                      <div className="text-xs text-white/50">Recherche d'un accord</div>
                    </button>
                    <button
                      onClick={() => setDiscussionMode('comparison')}
                      className={`p-4 rounded-xl text-left ${discussionMode === 'comparison' ? 'bg-blue-500/20 border border-blue-500' : 'glass-card'}`}
                    >
                      <div className="font-semibold text-white mb-1">Comparaison</div>
                      <div className="text-xs text-white/50">Convergences/Divergences</div>
                    </button>
                  </div>
                </div>
                <div>
                  <label className="label text-white/50 mb-3 block">Nombre de tours : {discussionRounds}</label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={discussionRounds}
                    onChange={(e) => setDiscussionRounds(parseInt(e.target.value))}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                <div>
                  <label className="label text-white/50 mb-3 block">IA participantes</label>
                  <div className="flex flex-wrap gap-2">
                    {thread.length > 0 && thread[thread.length - 1].responses.map(r => {
                      const config = AI_CONFIG[r.ai_key] || { color: '#8B909A', name: r.ai_name };
                      const isSelected = discussionAIs.includes(r.ai_key);
                      return (
                        <button
                          key={r.ai_key}
                          onClick={() => {
                            setDiscussionAIs(prev =>
                              prev.includes(r.ai_key)
                                ? prev.filter(a => a !== r.ai_key)
                                : [...prev, r.ai_key]
                            );
                          }}
                          className={`px-3 py-2 rounded-lg flex items-center gap-2 ${isSelected ? 'bg-white/10 border border-white/20' : 'glass-card opacity-50'}`}
                        >
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: config.color }} />
                          <span className="text-sm text-white">{config.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="p-6 border-t border-white/5 flex justify-end gap-3">
                <button onClick={() => setShowDiscussionSettings(false)} className="btn-secondary">Annuler</button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  onClick={handleDiscussion}
                  disabled={discussionAIs.length < 2}
                  className="btn-glow text-white font-semibold px-6 py-3 rounded-full disabled:opacity-50"
                >
                  Lancer la discussion
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* History Modal */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setShowHistory(false)}
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="relative w-full max-w-2xl max-h-[80vh] glass rounded-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-white/5 flex justify-between">
                <h2 className="font-outfit font-bold text-xl text-white">Historique des conversations</h2>
                <button onClick={() => setShowHistory(false)} className="p-2 rounded-full hover:bg-white/5">
                  <X className="w-5 h-5 text-white/50" />
                </button>
              </div>
              <div className="overflow-y-auto max-h-[60vh] p-4 space-y-3">
                {conversations.length === 0 ? (
                  <p className="text-center text-white/40 py-8">Aucune conversation</p>
                ) : (
                  conversations.map(conv => (
                    <button
                      key={conv.conversation_id}
                      onClick={() => loadConversation(conv.conversation_id)}
                      className="w-full text-left p-4 rounded-xl glass-card hover:border-white/20"
                    >
                      <p className="text-white text-sm mb-2 line-clamp-2">{conv.question}</p>
                      <div className="flex items-center gap-2">
                        {conv.selected_ais?.map(ai => (
                          <div key={ai} className="w-2 h-2 rounded-full" style={{ backgroundColor: AI_CONFIG[ai]?.color }} />
                        ))}
                        <span className="text-xs text-white/30 ml-2">
                          {new Date(conv.created_at).toLocaleDateString('fr-FR')}
                        </span>
                        {conv.is_thread && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">Thread</span>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
